import { debug, Debugger } from 'debug';
import { getSandboxEnvironment, root, ISandboxEnvironment } from '@estee/elc-universal-utils';
import * as Sentry from '@sentry/browser';

export interface IFullData {
    logdata: ILogData;
    level: string;
    config: IELCLogger;
}

export interface IPayload {
    error?: Error;
    context?: object;
    serviceName?: string;
    serviceVersion?: string;
}

export interface ILogData {
    triggerType: TriggerType;
    message: string;
    payload?: IPayload;
}

export enum TriggerType {
    runtimeError = 'runtimeError',
    api = 'api',
    graphqlApi = 'graphqlApi',
    render = 'render',
    serviceBus = 'serviceBus',
    config = 'config',
    translation = 'translation',
    other = 'other'
}

export interface IELCLogger {
    serviceName: string;
    serviceVersion?: string;
    environment?: string;
    buid?: string;
}

export class ELCLogger {
    private config: IELCLogger;

    private errorDebugger?: Debugger;
    private infoDebugger?: Debugger;
    private warningDebugger?: Debugger;
    private eventDebugger?: Debugger;
    private queryDebugger?: Debugger;
    private commandDebugger?: Debugger;

    constructor(config: IELCLogger) {
        this.config = config;
        const logger = debug(this.config.serviceName);

        this.errorDebugger = logger.extend('error');
        this.infoDebugger = logger.extend('info');
        this.warningDebugger = logger.extend('warning');
        this.eventDebugger = logger.extend('event');
        this.queryDebugger = logger.extend('query');
        this.commandDebugger = logger.extend('command');
    }

    public error = async (logdata: ILogData) => {
        if (this.errorDebugger) {
            this.errorDebugger(this.enhanceLogData(logdata));
        }

        const isProd =
            this.config.environment === ISandboxEnvironment.PRODUCTION ||
            root.env === ISandboxEnvironment.PRODUCTION;

        if ((isProd && getSandboxEnvironment() === ISandboxEnvironment.LIVE) || !isProd) {
            return this.sendLog(logdata, 'error');
        }
    };

    public info = (logdata: ILogData) => {
        if (this.infoDebugger) {
            this.infoDebugger(this.enhanceLogData(logdata));
        }
    };

    public warning = (logdata: ILogData) => {
        if (this.warningDebugger) {
            this.warningDebugger(this.enhanceLogData(logdata));
        }
    };

    public event = (logdata: ILogData) => {
        if (this.eventDebugger) {
            this.eventDebugger(this.enhanceLogData(logdata));
        }
    };

    public query = (logdata: ILogData) => {
        if (this.queryDebugger) {
            this.queryDebugger(this.enhanceLogData(logdata));
        }
    };

    public command = (logdata: ILogData) => {
        if (this.commandDebugger) {
            this.commandDebugger(this.enhanceLogData(logdata));
        }
    };

    private enhanceLogData = (logdata: ILogData) => ({
        ...logdata,
        triggerSource: this.config.serviceName,
        date: new Date().toUTCString()
    });
    // @ts-ignore
    private async sendLog(logdata: ILogData, level: string) {
        try {
            root.__SENTRY__
                ? Sentry.captureMessage(JSON.stringify(logdata))
                : console.info('Logging failed because Sentry is not available');
        } catch (e) {
            if (this.errorDebugger) {
                this.errorDebugger(
                    this.enhanceLogData({
                        message: 'Could not send log',
                        triggerType: TriggerType.api,
                        payload: {
                            error: e,
                            context: logdata
                        }
                    })
                );
            }
        }
    }
}
